import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Ban, ShieldOff, Trash2, RefreshCw, Search, ShieldCheck, Clock, User } from "lucide-react";
import Loading from "./Loading";

function fmt(iso) { try { return new Date(iso).toLocaleString(); } catch { return "—"; } }

export default function AdminUsers() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setRows(r.data?.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "FAILED TO LOAD PILOTS");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const ban = async (u) => {
    if (!window.confirm(`Ban @${u.call_sign}? They will be locked out.`)) return;
    try { await api.post(`/admin/users/${u.id}/ban`); toast.success("PILOT BANNED"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "BAN FAILED"); }
  };
  const unban = async (u) => {
    try { await api.post(`/admin/users/${u.id}/unban`); toast.success("PILOT REINSTATED"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "UNBAN FAILED"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Permanently delete @${u.call_sign}? Their favorites will also be purged.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("PILOT DELETED"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "DELETE FAILED"); }
  };

  const filtered = rows.filter(u => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (u.call_sign || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  });

  return (
    <section data-testid="admin-users">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-1 max-w-md border border-[color:var(--sat-border)] focus-within:border-[color:var(--sat-cyan)] px-3 py-2">
          <Search size={13} className="sat-cyan" />
          <input
            data-testid="admin-users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="SEARCH BY CALLSIGN OR EMAIL"
            className="flex-1 bg-transparent outline-none font-mono text-sm placeholder:text-[color:var(--sat-text-dim)]"
          />
        </div>
        <button onClick={load} className="sat-btn !py-2 !px-3" data-testid="admin-users-refresh">
          <RefreshCw size={11} /> REFRESH
        </button>
        <div className="sat-label ml-auto">{filtered.length} / {rows.length} PILOTS</div>
      </div>

      {loading && <Loading message="LOADING PILOTS" />}
      {!loading && filtered.length === 0 && (
        <div className="sat-label text-center py-10" data-testid="admin-users-empty">NO MATCHES</div>
      )}

      <div className="sat-surface border border-[color:var(--sat-border)] overflow-x-auto">
        <table className="w-full text-sm font-mono" data-testid="admin-users-table">
          <thead>
            <tr className="border-b border-[color:var(--sat-border)] sat-label text-left">
              <th className="px-3 py-2">CALLSIGN</th>
              <th className="px-3 py-2">EMAIL</th>
              <th className="px-3 py-2">JOINED</th>
              <th className="px-3 py-2">LAST SEEN</th>
              <th className="px-3 py-2">STATUS</th>
              <th className="px-3 py-2 text-right">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-b border-[color:var(--sat-border)] hover:bg-black/40" data-testid={`admin-user-row-${u.id}`}>
                <td className="px-3 py-2">
                  <span className="flex items-center gap-1.5">
                    <User size={11} className="sat-cyan" />
                    <span className="sat-cyan">@{u.call_sign || "—"}</span>
                    {u.is_admin && <ShieldCheck size={11} className="text-amber-400" title="Admin" />}
                  </span>
                </td>
                <td className="px-3 py-2 text-[color:var(--sat-text-dim)]">{u.email}</td>
                <td className="px-3 py-2 text-[color:var(--sat-text-dim)] text-xs">{fmt(u.created_at)}</td>
                <td className="px-3 py-2 text-[color:var(--sat-text-dim)] text-xs">
                  <Clock size={10} className="inline mr-1" /> {u.last_seen ? fmt(u.last_seen) : "—"}
                </td>
                <td className="px-3 py-2">
                  {u.is_banned
                    ? <span className="text-red-400">BANNED</span>
                    : u.is_admin
                      ? <span className="text-amber-400">ADMIN</span>
                      : <span className="sat-cyan">ACTIVE</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {u.is_admin ? (
                    <span className="sat-label text-[color:var(--sat-text-dim)]">PROTECTED</span>
                  ) : u.is_banned ? (
                    <>
                      <button onClick={() => unban(u)} className="sat-btn !py-1 !px-2 mr-1"
                        data-testid={`admin-user-unban-${u.id}`} title="Reinstate">
                        <ShieldOff size={11} /> UNBAN
                      </button>
                      <button onClick={() => del(u)} className="sat-btn sat-btn-danger !py-1 !px-2"
                        data-testid={`admin-user-delete-${u.id}`} title="Delete">
                        <Trash2 size={11} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => ban(u)} className="sat-btn !py-1 !px-2 mr-1"
                        data-testid={`admin-user-ban-${u.id}`} title="Ban">
                        <Ban size={11} /> BAN
                      </button>
                      <button onClick={() => del(u)} className="sat-btn sat-btn-danger !py-1 !px-2"
                        data-testid={`admin-user-delete-${u.id}`} title="Delete">
                        <Trash2 size={11} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
